import {
  Skeleton,
  CardContent,
  Card,
  Divider,
  Tooltip,
  styled,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { InfoOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { numberToCurrencyString, renderMultiple } from 'further-ui/utils';
import { INVESTMENT, ALL_FUNDS } from 'adminConstants/routes';
import useLifetimePerformanceSummary from 'hooks/data/dashboards/useLifetimePerfomanceSummary';
import Heading from 'components/Heading';
import { Margin, Row } from 'components/Layout';
import LoadingError from '../LoadingError';
import useInitialElementSize from 'hooks/ui/useInitialElementSize';
import FundTypeSelector from '../FundTypeSelector';
import { useFundTypeSelectorOptionsContext } from '../FundTypeSelector/FundTypeSelectorOptionsContext';
import { useSelectedFundTypeState } from '../FundTypeSelector/useSelectedFundTypeState';

const HeadingDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(5),
}));

const useStyles = makeStyles()(() => ({
  boxContainer: {
    height: '100%',
    '@media (min-width: 600px)': {
      display: 'grid',
      gap: '20px',
      gridTemplateColumns: 'repeat(2, 1fr)',
      marginBottom: '0',
    },
  },
  box: {
    background: '#fafafa',
    borderRadius: '4px',
    padding: '20px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: '0 0 calc(50% - 10px)',
    '&:not(:last-of-type)': {
      marginBottom: '20px',
    },
    '@media (min-width: 600px)': {
      margin: '0 !important',
    },
  },
  boxTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  boxSubtitle: {
    fontSize: '14px',
    opacity: 0.6,
    display: 'flex',
    alignItems: 'center',
  },
  tooltipIcon: {
    cursor: 'pointer',
    fontSize: '13px',
    margin: '-2px 0 0 5px',
  },
}));

const LifetimePerformanceSummary = ({ parentClasses, firmId }) => {
  const { classes } = useStyles();

  const { options: fundTypeOptions } = useFundTypeSelectorOptionsContext();
  const {
    selectedFundTypes,
    allFundsSelected,
    setSelectedFundTypes,
    selectedFundTypeTagIds,
  } = useSelectedFundTypeState(fundTypeOptions);

  const { data, isFetching, isRefetching, isError } =
    useLifetimePerformanceSummary({
      firmId,
      tagIds: allFundsSelected ? [] : selectedFundTypeTagIds,
    });

  const isLoading = isFetching || isRefetching;

  const [ref, { height }] = useInitialElementSize();
  if (isError) {
    return <LoadingError height={height} />;
  }

  return (
    <Card ref={ref}>
      <CardContent>
        <Margin bottom="md">
          <Row justify="space-between" centered>
            <Heading
              variant="h3"
              noMargin
              subtitle={`As of ${new Date().toLocaleDateString('en-GB')}`}
            >
              Lifetime performance
            </Heading>
            <div style={{ width: '180px' }}>
              <FundTypeSelector
                allOptionsSelected={allFundsSelected}
                options={fundTypeOptions}
                value={selectedFundTypes}
                onChange={(newSelectedFundType) =>
                  setSelectedFundTypes(newSelectedFundType)
                }
              />
            </div>
          </Row>
        </Margin>
        <HeadingDivider />
        <div className={classes.boxContainer}>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                numberToCurrencyString(data?.totalInvestmentAmount || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>Total capital raised</div>
            <div className={parentClasses.linkContainer}>
              <Link to={INVESTMENT} className={parentClasses.link}>
                View all subscriptions
              </Link>
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                numberToCurrencyString(data?.capitalAwaitingDeployment || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>
              Capital awaiting deployment
            </div>
            <div className={parentClasses.linkContainer}>
              <Link
                to={`${ALL_FUNDS}?fundStatus=closed-in-deployment`}
                className={parentClasses.link}
              >
                View undeployed tranches
              </Link>
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                numberToCurrencyString(data?.lifetimeAssetTotalValue || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>
              Lifetime asset total value
              <Tooltip
                title={`This is the sum of current value AUM and total net realised value.`}
              >
                <InfoOutlined className={classes.tooltipIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                renderMultiple(data?.TVPI || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>TVPI</div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                numberToCurrencyString(data?.currentValueAum || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>
              Current value AUM{' '}
              <Tooltip
                title={`This is your current unrealised AUM, calculated as current investable cash plus the live value of existing holdings. The committed capital value of this AUM is ${numberToCurrencyString(
                  data?.currentCashAum || 0,
                )}.`}
              >
                <InfoOutlined className={classes.tooltipIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                renderMultiple(data?.RVPI || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>RVPI</div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                numberToCurrencyString(data?.totalNetRealisedValue || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>
              Total net realised value
              <Tooltip
                title={
                  'This is the total value returned to investors from all exits, dividends, residual capital and interest across all subscriptions.'
                }
              >
                <InfoOutlined className={classes.tooltipIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.boxTitle}>
              {isLoading ? (
                <Skeleton animation="wave" variant="text" />
              ) : (
                renderMultiple(data?.DPI || 0)
              )}
            </div>
            <div className={classes.boxSubtitle}>DPI</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LifetimePerformanceSummary;
